import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, Shield, UserCheck, UserX, RefreshCw, Search, 
  Trash2, Crown, Code, Eye, User, Bug, MessageSquare,
  FileText, BarChart3, Calendar, Clock, Check, Archive,
  RotateCcw, AlertTriangle, Gamepad2, Globe, History, GitBranch
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { SeverityBadge, StatusBadge } from "./Dashboard";
import { SlideToConfirm } from "@/components/SlideToConfirm";
import { ClusterDashboard } from "./ClusterDashboard";

const ROLES = [
  { value: "admin", label: "Admin", icon: Crown, color: "text-red-400" },
  { value: "developer", label: "Developer", icon: Code, color: "text-blue-400" },
  { value: "tester", label: "Tester", icon: Eye, color: "text-purple-400" },
  { value: "reporter", label: "Reporter", icon: User, color: "text-gray-400" },
];

// Protected accounts that cannot be deleted - must match backend PROTECTED_EMAILS
const PROTECTED_EMAILS = [
  "q.darkvoid@gmail.com"
];

function UserRow({ userData, onRoleChange, onDelete, currentUserId }) {
  const [loading, setLoading] = useState(false);
  const role = ROLES.find(r => r.value === userData.role) || ROLES[3];
  const Icon = role.icon;
  
  // Check if this is a protected account
  const isProtected = PROTECTED_EMAILS.some(
    email => email.toLowerCase() === userData.email?.toLowerCase()
  );

  const handleRoleChange = async (newRole) => {
    setLoading(true);
    await onRoleChange(userData.user_id, newRole);
    setLoading(false);
  };

  const handleDelete = async () => {
    if (isProtected) {
      toast.error("This account is protected and cannot be deleted");
      return;
    }
    if (!window.confirm(`Delete user ${userData.name}? This action cannot be undone.`)) return;
    setLoading(true);
    await onDelete(userData.user_id);
    setLoading(false);
  };

  const isCurrentUser = userData.user_id === currentUserId;

  return (
    <div className={`flex items-center justify-between p-4 bg-muted/20 rounded-sm border border-border/40 ${isCurrentUser ? 'border-primary/30 bg-primary/5' : ''} ${isProtected ? 'border-yellow-500/30' : ''}`}>
      <div className="flex items-center gap-4">
        {userData.picture ? (
          <img src={userData.picture} alt={userData.name} className="w-10 h-10 rounded-full" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-body font-semibold text-foreground">{userData.name}</p>
            {isCurrentUser && (
              <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm font-mono">YOU</span>
            )}
            {isProtected && (
              <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-sm font-mono flex items-center gap-1">
                <Shield className="w-2.5 h-2.5" />
                PROTECTED
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono">{userData.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-muted-foreground font-mono">
              {userData.auth_provider === 'discord' ? '🎮 Discord' : '🔐 Google'}
            </span>
            <span className="text-[10px] text-muted-foreground font-mono">
              • Joined {new Date(userData.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-1 px-2 py-1 rounded-sm bg-muted/50 ${role.color}`}>
          <Icon className="w-3 h-3" />
          <span className="text-xs font-mono">{role.label}</span>
        </div>
        
        {!isCurrentUser && (
          <>
            <Select value={userData.role} onValueChange={handleRoleChange} disabled={loading || isProtected}>
              <SelectTrigger className={`w-32 bg-black/20 border-border/50 font-mono text-xs ${isProtected ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    <span className={`flex items-center gap-2 ${r.color}`}>
                      <r.icon className="w-3 h-3" />
                      {r.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Hide delete button for protected accounts */}
            {!isProtected && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                disabled={loading}
                className="text-muted-foreground hover:text-destructive"
                data-testid="delete-user-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border/40">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-mono uppercase">{title}</p>
            <p className={`text-2xl font-heading font-bold ${color}`}>{value}</p>
          </div>
          <Icon className={`w-8 h-8 ${color} opacity-50`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPanel() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Bug History state
  const [bugHistory, setBugHistory] = useState([]);
  const [bugHistoryStats, setBugHistoryStats] = useState({ total: 0, deleted_count: 0, active_count: 0 });
  const [bugFilter, setBugFilter] = useState("deleted"); // deleted, active, all
  const [bugGameFilter, setBugGameFilter] = useState("all");
  const [bugSearch, setBugSearch] = useState("");
  const [bugHistoryLoading, setBugHistoryLoading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState(null);
  const [generatedKey, setGeneratedKey] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/feedback`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setFeedback(feedbackRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, fetchData]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchData();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleFeedbackStatus = async (feedbackId, status) => {
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status`, { status }, { withCredentials: true });
      toast.success(`Feedback marked as ${status}`);
      fetchData();
    } catch (error) {
      console.error("Error updating feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to update feedback");
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm("Delete this feedback? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/feedback/${feedbackId}`, { withCredentials: true });
      toast.success("Feedback deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to delete feedback");
    }
  };

  // Bug History functions
  const fetchBugHistory = async () => {
    try {
      setBugHistoryLoading(true);
      const params = { include_deleted: true, limit: 500 };
      if (bugGameFilter !== "all") params.game = bugGameFilter;
      const res = await axios.get(`${API}/admin/bugs/all`, { params, withCredentials: true });
      setBugHistory(res.data.bugs || []);
      setBugHistoryStats({
        total: res.data.total || 0,
        deleted_count: res.data.deleted_count || 0,
        active_count: res.data.active_count || 0,
      });
    } catch (error) {
      console.error("Error fetching bug history:", error);
      toast.error("Failed to load bug history");
    } finally {
      setBugHistoryLoading(false);
    }
  };

  const handleRestore = async (bugId) => {
    try {
      await axios.post(`${API}/bugs/${bugId}/restore`, {}, { withCredentials: true });
      toast.success("Bug restored successfully");
      fetchBugHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore bug");
    }
  };

  const handleHardDelete = async (bugId) => {
    try {
      await axios.delete(`${API}/bugs/${bugId}?hard_delete=true`, { withCredentials: true });
      toast.success("Bug permanently deleted");
      fetchBugHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to hard delete bug");
    }
  };

  const handleDeleteAllBugs = async () => {
    try {
      setDeletingAll(true);
      const bugsToDelete = filteredBugHistory.filter(b => b.is_deleted);
      let deleted = 0;
      for (const bug of bugsToDelete) {
        await axios.delete(`${API}/bugs/${bug.id}?hard_delete=true`, { withCredentials: true });
        deleted++;
      }
      toast.success(`${deleted} bugs permanently deleted`);
      fetchBugHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete all bugs");
    } finally {
      setDeletingAll(false);
    }
  };

  const filteredBugHistory = bugHistory.filter(b => {
    if (bugFilter === "deleted" && !b.is_deleted) return false;
    if (bugFilter === "active" && b.is_deleted) return false;
    if (bugGameFilter !== "all" && b.game !== bugGameFilter) return false;
    if (bugSearch) {
      const q = bugSearch.toLowerCase();
      return (b.title?.toLowerCase().includes(q) || b.display_code?.toLowerCase().includes(q) || b.id?.toLowerCase().includes(q));
    }
    return true;
  });

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = !searchQuery || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (authLoading) {
    return (
      <div className="p-8 flex items-center justify-center" data-testid="admin-auth-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold uppercase tracking-wider text-foreground mb-2">
          Authentication Required
        </h2>
        <p className="text-muted-foreground font-body">Please sign in to access the admin panel.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const userRole = user?.role || "reporter";
  if (userRole !== "admin") {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold uppercase tracking-wider text-foreground mb-2">
          Access Denied
        </h2>
        <p className="text-muted-foreground font-body">You need admin privileges to access this panel.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="admin-panel">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          Admin Panel
        </h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm">Manage users, view statistics, and review feedback</p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard title="Total Users" value={stats.total_users} icon={Users} color="text-blue-400" />
          <StatCard title="Total Bugs" value={stats.total_bugs} icon={Bug} color="text-red-400" />
          <StatCard title="Feedback" value={stats.total_feedback} icon={MessageSquare} color="text-yellow-400" />
          <StatCard title="Comments" value={stats.total_comments} icon={FileText} color="text-green-400" />
        </div>
      )}

      <Tabs defaultValue="clusters" className="w-full">
        <TabsList className="w-full rounded-none border-b border-border/40 bg-muted/20 p-0 h-auto mb-6">
          <TabsTrigger
            value="clusters"
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
          >
            <GitBranch className="w-4 h-4 mr-2" />
            Clusters
          </TabsTrigger>
          <TabsTrigger
            value="bug-history"
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
            onClick={() => { if (bugHistory.length === 0) fetchBugHistory(); }}
          >
            <History className="w-4 h-4 mr-2" />
            Bug History
          </TabsTrigger>
          <TabsTrigger
            value="stats"
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Statistics
          </TabsTrigger>
          <TabsTrigger
            value="feedback"
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Feedback ({feedback.length})
          </TabsTrigger>
          <TabsTrigger
            value="users"
            className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 font-heading font-bold uppercase tracking-wider text-xs"
          >
            <Users className="w-4 h-4 mr-2" />
            Users ({users.length})
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="m-0">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users by name or email..."
                className="pl-10 bg-black/20 border-border/50"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40 bg-black/20 border-border/50">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} className="font-mono">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Users List */}
          <div className="space-y-3">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((u) => (
                <UserRow
                  key={u.user_id}
                  userData={u}
                  onRoleChange={handleRoleChange}
                  onDelete={handleDelete}
                  currentUserId={user?.user_id}
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground font-mono">
                No users found matching your criteria
              </div>
            )}
          </div>

          {/* API Keys Section */}
          <div className="mt-8 border-t border-border/40 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-heading font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Developer API Keys
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/api-keys/generate`, {}, { withCredentials: true });
                    toast.success("API Key generated! Copy it now — it won't be shown again.");
                    setGeneratedKey(res.data.api_key);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to generate key");
                  }
                }}
                className="font-mono text-xs"
                data-testid="generate-api-key-btn"
              >
                Generate New Key
              </Button>
            </div>
            {generatedKey && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-sm mb-4">
                <p className="text-[10px] text-emerald-400 font-mono uppercase mb-1">New API Key (copy now — shown only once)</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-emerald-300 font-mono bg-black/30 px-2 py-1 rounded-sm flex-1 break-all">{generatedKey}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(generatedKey); toast.success("Copied!"); }}
                    className="text-emerald-400 hover:text-emerald-300 h-7"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground font-mono">
              API keys allow external tools to update bug statuses via: PATCH /api/bugs/BUG_ID with Authorization: Bearer YOUR_KEY
            </p>
          </div>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="m-0">
          <div className="space-y-4">
            {feedback.length > 0 ? (
              feedback.map((f) => (
                <Card key={f.id} className={`bg-card border-border/40 ${f.status === 'handled' ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-mono uppercase ${
                            f.type === 'feature' ? 'bg-yellow-500/20 text-yellow-400' :
                            f.type === 'bug' ? 'bg-red-500/20 text-red-400' :
                            f.type === 'improvement' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-purple-500/20 text-purple-400'
                          }`}>
                            {f.type}
                          </span>
                          {f.status === 'handled' && (
                            <span className="text-xs px-2 py-0.5 rounded-sm font-mono uppercase bg-green-500/20 text-green-400">
                              Handled
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground font-mono">
                            {new Date(f.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h3 className="font-heading font-bold text-foreground mb-1">{f.title}</h3>
                        <p className="text-sm text-muted-foreground font-body">{f.description}</p>
                        {f.contact_email && (
                          <p className="text-xs text-primary font-mono mt-2">Contact: {f.contact_email}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {f.status !== 'handled' ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFeedbackStatus(f.id, 'handled')}
                            className="text-xs font-mono text-green-400 border-green-500/30 hover:bg-green-500/10"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Mark Handled
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFeedbackStatus(f.id, 'pending')}
                            className="text-xs font-mono text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10"
                          >
                            <Archive className="w-3 h-3 mr-1" />
                            Reopen
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteFeedback(f.id)}
                          className="text-xs font-mono text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground font-mono">
                No feedback received yet
              </div>
            )}
          </div>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats" className="m-0">
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-card border-border/40">
                <CardHeader className="border-b border-border/40 bg-muted/20">
                  <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Users by Role
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {ROLES.map((role) => {
                      const count = stats.users_by_role[role.value] || 0;
                      const percentage = stats.total_users > 0 ? (count / stats.total_users) * 100 : 0;
                      return (
                        <div key={role.value} className="flex items-center justify-between">
                          <div className={`flex items-center gap-2 ${role.color}`}>
                            <role.icon className="w-4 h-4" />
                            <span className="font-mono text-sm">{role.label}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full bg-primary transition-all`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="font-mono text-sm text-foreground w-8 text-right">{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border/40">
                <CardHeader className="border-b border-border/40 bg-muted/20">
                  <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Activity Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-sm">
                      <span className="font-mono text-sm text-muted-foreground">Bugs (Last 7 Days)</span>
                      <span className="font-mono text-lg font-bold text-foreground">{stats.bugs_last_7_days}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-sm">
                      <span className="font-mono text-sm text-muted-foreground">Total Feedback</span>
                      <span className="font-mono text-lg font-bold text-foreground">{stats.total_feedback}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/20 rounded-sm">
                      <span className="font-mono text-sm text-muted-foreground">Total Comments</span>
                      <span className="font-mono text-lg font-bold text-foreground">{stats.total_comments}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Bug History Tab */}
        <TabsContent value="bug-history" className="m-0">
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard title="Active Bugs" value={bugHistoryStats.active_count} icon={Bug} color="text-emerald-400" />
            <StatCard title="Deleted Bugs" value={bugHistoryStats.deleted_count} icon={Trash2} color="text-red-400" />
            <StatCard title="Total (All)" value={bugHistoryStats.active_count + bugHistoryStats.deleted_count} icon={Archive} color="text-blue-400" />
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={bugSearch}
                onChange={(e) => setBugSearch(e.target.value)}
                placeholder="Search by title or bug code..."
                className="pl-10 bg-black/20 border-border/50"
                data-testid="bug-history-search"
              />
            </div>
            <Select value={bugFilter} onValueChange={setBugFilter}>
              <SelectTrigger className="w-40 bg-black/20 border-border/50" data-testid="bug-history-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deleted">Deleted Only</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="all">All Bugs</SelectItem>
              </SelectContent>
            </Select>
            <Select value={bugGameFilter} onValueChange={(v) => { setBugGameFilter(v); }}>
              <SelectTrigger className="w-44 bg-black/20 border-border/50" data-testid="bug-history-game-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Games</SelectItem>
                <SelectItem value="STAR_ATLAS_UE5">UE5</SelectItem>
                <SelectItem value="STAR_ATLAS_HOLOSIM">Holosim</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchBugHistory} className="font-mono" data-testid="bug-history-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Bug List */}
          {bugHistoryLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : filteredBugHistory.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground font-mono">
              {bugHistory.length === 0 ? "Click Refresh to load bug history" : "No bugs match your filters"}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Delete All Slider */}
              {filteredBugHistory.filter(b => b.is_deleted).length > 0 && (
                <div className="p-3 bg-red-950/20 border border-red-500/20 rounded-sm" data-testid="delete-all-section">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-red-400 font-mono uppercase">
                      Permanently delete all soft-deleted bugs ({filteredBugHistory.filter(b => b.is_deleted).length})
                    </p>
                    {deletingAll && <RefreshCw className="w-3 h-3 animate-spin text-red-400" />}
                  </div>
                  <SlideToConfirm
                    onConfirm={handleDeleteAllBugs}
                    label="Slide to delete all"
                    variant="destructive"
                    disabled={deletingAll}
                  />
                </div>
              )}

              {filteredBugHistory.map((bug) => (
                <div
                  key={bug.id}
                  className={`flex items-center gap-4 p-3 rounded-sm border transition-colors ${
                    bug.is_deleted
                      ? "bg-red-950/10 border-red-500/20 opacity-80"
                      : "bg-muted/20 border-border/40"
                  }`}
                  data-testid={`bug-history-row-${bug.id}`}
                >
                  {/* Code + Title */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link
                        to={`/bugs/${bug.id}`}
                        className="font-mono text-xs text-primary hover:underline flex-shrink-0"
                        data-testid={`bug-history-link-${bug.id}`}
                      >
                        {bug.display_code || bug.id?.slice(0, 8)}
                      </Link>
                      <span className={`text-sm font-medium truncate ${bug.is_deleted ? "line-through text-muted-foreground" : "text-foreground"}`}>
                        {bug.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {bug.game === "STAR_ATLAS_HOLOSIM" ? "HOLOSIM" : "UE5"}
                      </span>
                      <SeverityBadge severity={bug.severity} />
                      <StatusBadge status={bug.status} />
                      {bug.is_deleted && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-mono uppercase">
                          Deleted {bug.deleted_at ? new Date(bug.deleted_at).toLocaleDateString() : ""} {bug.deleted_by ? `by ${bug.deleted_by}` : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {bug.is_deleted ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(bug.id)}
                          className="text-xs font-mono text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                          data-testid={`restore-btn-${bug.id}`}
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Restore
                        </Button>
                        {hardDeleteConfirm === bug.id ? (
                          <div className="w-48">
                            <SlideToConfirm
                              onConfirm={() => { handleHardDelete(bug.id); setHardDeleteConfirm(null); }}
                              label="Slide to delete"
                              variant="destructive"
                            />
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setHardDeleteConfirm(bug.id)}
                            className="text-xs font-mono text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`hard-delete-btn-${bug.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Permanent
                          </Button>
                        )}
                      </>
                    ) : (
                      <Link
                        to={`/bugs/${bug.id}`}
                        className="text-xs font-mono text-primary hover:underline"
                      >
                        View
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Clusters Tab */}
        <TabsContent value="clusters" className="m-0">
          <ClusterDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
